import { useEffect, useState } from 'react'
import { useAuth } from './useAuth'
import { fetchWithAuth } from '../services/api/fetchWithAuth'
import type { UseFetchResult } from './useFetch.types'

export type { UseFetchResult }

const inflightRequests = new Map<string, Promise<Response>>()

export function useFetch<T>(
  path: string,
  initialData: T,
  transform?: (json: unknown) => T
): UseFetchResult<T> {
  const { getToken } = useAuth()
  const [data, setData] = useState<T>(initialData)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        let responsePromise = inflightRequests.get(path)
        if (!responsePromise) {
          const token = await getToken()
          responsePromise = fetchWithAuth(path, token)
          inflightRequests.set(path, responsePromise)
        }

        const response = await responsePromise
        const json = await response.clone().json()
        inflightRequests.delete(path)

        if (!cancelled) {
          setData(transform ? transform(json) : (json as T))
        }
      } catch (err) {
        inflightRequests.delete(path)
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : `Failed to fetch ${path}`
          )
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [path, getToken, transform])

  return { data, isLoading, error }
}
